/**
 * Mock fixtures for the Teacher dashboard shell.
 *
 * Sprint 1 ships only the dashboard *shell* — there is no real Worker API
 * powering courseware-draft / grading-draft endpoints yet. The UI pulls
 * hand-curated fixtures from this module in mock mode so every page has
 * a non-empty render path AND an empty render path (controlled per-call).
 *
 * Hard rules encoded here:
 *   - Student identities are pseudonyms (`Student-001`). No real names, no
 *     emails are surfaced to the UI.
 *   - Statuses are limited to `draft` / `needs_review`. There is no
 *     `published`, `final`, or `auto_published` state in this module.
 */

import type {
  UiAssignment,
  UiAssistantMessage,
  UiCourse,
  UiCoursewareDraft,
  UiGradingDraft,
  UiSettings,
  UiStudentCourse,
  UiStudentProfile,
  UiStudentProgressEntry,
  UiTutorProblem,
} from './types';

export const MOCK_COURSES: ReadonlyArray<UiCourse> = [
  {
    id: 'course_alpha',
    name: 'Mock Algebra I',
    code: 'MOCK-MATH-101',
    termName: 'Mock University — Spring 2026',
    studentCount: 24,
  },
  {
    id: 'course_beta',
    name: 'Mock World History',
    code: 'MOCK-HIST-101',
    termName: 'Mock University — Spring 2026',
    studentCount: 18,
  },
];

export const MOCK_ASSIGNMENTS: ReadonlyArray<UiAssignment> = [
  {
    id: 'asg_alpha_001',
    courseId: 'course_alpha',
    title: 'Linear equations — practice set 1',
    dueAt: '2026-02-12T23:59:00Z',
    pointsPossible: 20,
  },
  {
    id: 'asg_alpha_002',
    courseId: 'course_alpha',
    title: 'Word problems — quiz',
    dueAt: '2026-02-19T23:59:00Z',
    pointsPossible: 30,
  },
  {
    id: 'asg_beta_001',
    courseId: 'course_beta',
    title: 'Bronze Age trade routes — short answer',
    dueAt: '2026-02-15T23:59:00Z',
    pointsPossible: 25,
  },
];

export const MOCK_COURSEWARE_DRAFTS: ReadonlyArray<UiCoursewareDraft> = [
  {
    id: 'cwd_001',
    courseId: 'course_alpha',
    courseName: 'Mock Algebra I',
    title: 'Unit plan: linear equations (week 3)',
    status: 'needs_review',
    updatedAt: '2026-04-28T15:42:00Z',
  },
  {
    id: 'cwd_002',
    courseId: 'course_alpha',
    courseName: 'Mock Algebra I',
    title: 'Worksheet: word problems warm-up',
    status: 'draft',
    updatedAt: '2026-04-29T09:10:00Z',
  },
  {
    id: 'cwd_003',
    courseId: 'course_beta',
    courseName: 'Mock World History',
    title: 'Discussion prompts: trade routes',
    status: 'draft',
    updatedAt: '2026-04-29T16:00:00Z',
  },
];

export const MOCK_GRADING_DRAFTS: ReadonlyArray<UiGradingDraft> = [
  {
    id: 'gd_001',
    assignmentId: 'asg_alpha_001',
    assignmentTitle: 'Linear equations — practice set 1',
    courseName: 'Mock Algebra I',
    studentPseudonym: 'Student-001',
    status: 'needs_review',
    updatedAt: '2026-04-29T18:22:00Z',
  },
  {
    id: 'gd_002',
    assignmentId: 'asg_alpha_001',
    assignmentTitle: 'Linear equations — practice set 1',
    courseName: 'Mock Algebra I',
    studentPseudonym: 'Student-002',
    status: 'needs_review',
    updatedAt: '2026-04-29T18:30:00Z',
  },
  {
    id: 'gd_003',
    assignmentId: 'asg_beta_001',
    assignmentTitle: 'Bronze Age trade routes — short answer',
    courseName: 'Mock World History',
    studentPseudonym: 'Student-003',
    status: 'draft',
    updatedAt: '2026-04-30T08:15:00Z',
  },
];

export const MOCK_SETTINGS: UiSettings = {
  tenantId: 'tenant_mock',
  lmsProvider: 'mock',
  notifications: {
    gradingDraftReady: true,
    coursewareDraftReady: false,
  },
};

/* ---- Student-side fixtures ------------------------------------------ */

export const MOCK_STUDENT_PROFILE: UiStudentProfile = {
  pseudonym: 'Student-001',
  displayName: 'Student-001',
};

export const MOCK_STUDENT_COURSES: ReadonlyArray<UiStudentCourse> = [
  {
    id: 'course_alpha',
    name: 'Mock Algebra I',
    code: 'MOCK-MATH-101',
    termName: 'Mock University — Spring 2026',
    progressPercent: 64,
    status: 'in_progress',
    openAssignments: 2,
  },
  {
    id: 'course_beta',
    name: 'Mock World History',
    code: 'MOCK-HIST-101',
    termName: 'Mock University — Spring 2026',
    progressPercent: 30,
    status: 'in_progress',
    openAssignments: 1,
  },
];

export const MOCK_TUTOR_PROBLEMS: ReadonlyArray<UiTutorProblem> = [
  {
    id: 'tp_alpha_1',
    courseId: 'course_alpha',
    courseName: 'Mock Algebra I',
    title: 'Solve a one-step linear equation',
    prompt:
      'A bag of marbles costs $4. Sam paid $24 in total. Set up an equation for the number of bags Sam bought, then think about how to isolate the variable.',
    subject: 'math',
  },
  {
    id: 'tp_alpha_2',
    courseId: 'course_alpha',
    courseName: 'Mock Algebra I',
    title: 'Word problem — distance and time',
    prompt:
      'A train leaves the station at 9:00 a.m. travelling at 60 km/h. What information would you need to figure out how far it has gone by 11:30 a.m.?',
    subject: 'math',
  },
  {
    id: 'tp_beta_1',
    courseId: 'course_beta',
    courseName: 'Mock World History',
    title: 'Trade-route reasoning',
    prompt:
      'Why might a Bronze Age city build its market near a river instead of further inland? Note one factor and one trade-off.',
    subject: 'history',
  },
];

export const MOCK_ASSISTANT_TRANSCRIPT: ReadonlyArray<UiAssistantMessage> = [
  {
    id: 'am_001',
    role: 'student',
    content:
      'I keep mixing up combining like terms when there are negatives. Any way to keep track?',
    createdAt: '2026-04-29T13:02:00Z',
  },
  {
    id: 'am_002',
    role: 'assistant',
    content:
      "Draft suggestion: try underlining each term with its sign attached (so -3x stays linked to its minus). Then group the underlined pieces by variable. Want to walk through one together using your homework's problem 4?",
    createdAt: '2026-04-29T13:02:08Z',
  },
];

export const MOCK_STUDENT_PROGRESS: ReadonlyArray<UiStudentProgressEntry> = [
  {
    courseId: 'course_alpha',
    courseName: 'Mock Algebra I',
    progressPercent: 64,
    assignmentsCompleted: 7,
    assignmentsTotal: 11,
    lastActivityAt: '2026-04-29T18:22:00Z',
  },
  {
    courseId: 'course_beta',
    courseName: 'Mock World History',
    progressPercent: 30,
    assignmentsCompleted: 3,
    assignmentsTotal: 10,
    lastActivityAt: '2026-04-28T09:15:00Z',
  },
];
